import React, {useEffect, useState, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './roles.module.scss';
import Header from '../../components/shared/header';
import { IconButton } from '@mui/material';
import classNames from 'classnames';
import { useRolesTable } from './hook/useRolesTable';
import { useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { useMediaQuery } from 'react-responsive';
import { Role } from '../../features/roles/types';
import { deleteRole, getRoles } from '../../features/roles';
import { getPermissions } from '../../features/permissions';
import { DeleteIconButton } from '../../components/shared/delete-icon-button';
import { NAVIGATION_ROUTES } from '../../routes/NavigationRoute';
import PageWrapperV2 from '../../components/shared/page-wrapper-v2';
import PageHeader from '../../components/shared/page-header';
import { SVGS } from '../../assets/images';
import CreateNewButtonV2 from '../../components/shared/create-new-button-v2';
import PageBody from '../../components/shared/page-body';
import { TypeFilter, DEFAULT_TYPE_FILTER_STATE } from '../Types/components/type-filter';
import TiminatorTableV2 from '../../components/shared/table-v2';
import PageTableContainer from '../../components/shared/page-table-container';
import IconTextLarge from '../../components/shared/large-icon-text';
import { Lx } from '../../i18n/consts';
import PageFooter from '../../components/shared/page-footer';
import { useInfinitePagination } from '../../hooks/useInfinitePagination';
import { DeleteDialog } from '../../components/shared/delete-dialog';
import { canManageRolePermissions, canManageRoles } from '../../features/profile/entityPermissions';
import { getProfile } from '../../features/profile';

export default function Roles() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<number>(-1);
    const [search, setSearch] = useState('');
    const isMobile = useMediaQuery({ maxWidth: 1000  });
    const rolesState = useAppSelector(store => store.roles);
    const profile = useAppSelector(store => store.profile);
    const permissions = useAppSelector(store => store.permissions);
    const hasMore = rolesState.items.length < rolesState.total;
    const canEditRoles = canManageRoles(profile);
    const canEditRolePermissions = rolesState.canShowAndEditPermissions || canManageRolePermissions(profile);

    const fetchRolesPage = useCallback((page: number) => {
        return dispatch(getRoles(page)).unwrap();
    }, [dispatch]);

    const { loadInitial, loadNext, isLoadingMore, isLoadingInitial, reset } = useInfinitePagination({
        hasMore,
        fetchPage: fetchRolesPage,
    });

    const [mobileView, setMobileView] = useState<'list' | 'filter'>('list');

    const handleMobileFilterClick = () => {
        setMobileView(prev => (prev === 'list' ? 'filter' : 'list'));
    };

    const handleMobileFilterClose = useCallback(() => {
        setMobileView('list');
    }, []);

    const handleFilterChange = useCallback(({ search: nextSearch, selectedStatus: nextStatus }: { search: string; selectedStatus: number }) => {
        setSearch(nextSearch);
        setSelectedStatus(nextStatus);
    }, []);

    useEffect(() => {
        loadInitial().catch(() => {/* handled elsewhere */});
    }, [loadInitial]);

    useEffect(() => {
        if (canEditRolePermissions && permissions.length === 0) {
            dispatch(getPermissions());
        }
    }, [canEditRolePermissions, dispatch, permissions.length]);

    useEffect(() => {
        if (!isMobile) {
            setMobileView('list');
        }
    }, [isMobile]);

    const { columns, data, isEmpty } = useRolesTable(
        search,
        selectedStatus,
        isMobile
    );
    const showEmptyState = !isLoadingInitial && isEmpty;

    const enhancedColumns = columns
        .filter(col => canEditRoles || col.id !== 'deleteButton')
        .map(col => {
            if (col.id === 'deleteButton') {
                return {
                    ...col,
                    format: (rowData: Role) => (
                        <DeleteIconButton
                            onClick={() => {
                                setSelectedRole(rowData);
                                setOpen(true);
                            }}
                        />
                    ),
                };
            }
            return col;
        });

    function onRowEdit(role: Role) {
        if (!canEditRoles) {
            return;
        }

        navigate(NAVIGATION_ROUTES.EDIT_ROLE, {
            state: { role }
        });
    }

    const handleDeleteCancel = () => {
        setOpen(false);
        setSelectedRole(null);
    };

    const handleDeleteConfirm = () => {
        if (selectedRole) {
            dispatch(deleteRole(selectedRole.id)).unwrap().then(async () => {
                await dispatch(getProfile()).unwrap();
                reset();
                loadInitial().catch(() => {/* handled elsewhere */});
            });
        }
        handleDeleteCancel();
    };

    const handleCreateNew = () => {
        if (!canEditRoles) {
            return;
        }

        navigate(NAVIGATION_ROUTES.CREATE_ROLE);
    }

    const isMobileFilterActive = isMobile && mobileView === 'filter';
    const hasMobileFooter = isMobile && mobileView === 'list' && canEditRoles;
    return <>
        <PageWrapperV2 hasMobileFooter={hasMobileFooter}>
            <PageHeader>
                <div className={styles.container}>
                    <div className={styles.containerItem}>
                        <Header headerLocalizationId='roles' loading={isLoadingInitial}>
                            <div className={styles.rolesCount}>{data?.length ?? 0}</div>
                        </Header>
                    </div>
                    <div className={styles.containerItem}>
                        {   
                            isMobile
                                ? !showEmptyState
                                    ? (
                                        <IconButton
                                            aria-label='filterIconButton'
                                            onClick={() => {
                                                if (isMobileFilterActive) {
                                                    handleFilterChange(DEFAULT_TYPE_FILTER_STATE);
                                                }
                                                handleMobileFilterClick();
                                            }}>
                                            <SVGS.FilterIcon className={classNames({[styles.active]: isMobileFilterActive})} />
                                        </IconButton>
                                    )
                                    : null
                                : canEditRoles ? <CreateNewButtonV2 onClick={handleCreateNew} /> : null
                        }
                    </div>
                </div>
            </PageHeader>
            <PageBody justifyContent={showEmptyState ? 'center' : 'flex-start'} alignItems='stretch'>
                {   
                    !showEmptyState
                        ? (
                            <TypeFilter
                                search={search}
                                selectedStatus={selectedStatus}
                                isMobile={isMobile}
                                mobileView={mobileView}
                                onFilterChange={handleFilterChange}
                                onMobileClose={handleMobileFilterClose}
                            >
                                <PageTableContainer>
                                    <TiminatorTableV2 
                                        onRowClick={canEditRoles ? onRowEdit : undefined} 
                                        columns={enhancedColumns}
                                        data={data}
                                        hasMore={hasMore}
                                        onLoadMore={loadNext}
                                        isLoadingMore={isLoadingMore} />
                                </PageTableContainer>
                            </TypeFilter>
                        )
                        : <IconTextLarge 
                            text={intl.formatMessage({id: Lx.Role.NO_ROLES})} 
                            subtext={intl.formatMessage({id: Lx.Role.CREATE_AND_USE_ROLE})} 
                            icon={<SVGS.RolesIcon />} />
                }
            </PageBody>
            {
                hasMobileFooter &&
                    <PageFooter>
                        <CreateNewButtonV2 onClick={handleCreateNew} />
                    </PageFooter>
            }
        </PageWrapperV2>
        <DeleteDialog 
            open={open} 
            onCancel={handleDeleteCancel} 
            onConfirmDelete={handleDeleteConfirm}
            deleteHeader={intl.formatMessage({id: Lx.Role.DELETE_ROLE})}
            deleteMessage={intl.formatMessage({id: Lx.Role.DELETE_ROLE_MESSAGE}, { name: selectedRole?.name })}
            title={intl.formatMessage({id: Lx.General.ROLE})}  />
    </>
}
